import { getDefaultClient } from '../../../client/defaultClient';
import { extractChainFromNormalizedName } from '../../wallets/walletProvider/extractChainFromNormalizedName';
import { splitWalletProviderKey } from '../../wallets/walletProvider/splitWalletProviderKey';
import { getWalletConnectCatalog } from '../getWalletConnectCatalog';
import type { WalletConnectCatalogWallet } from '../getWalletConnectCatalog/getWalletConnectCatalog.types';

type GetWalletConnectCatalogWalletByWalletProviderKeyParams = {
  walletProviderKey: string;
};

/**
 * Gets the WalletConnect catalog wallet entry for a given wallet provider key.
 *
 * This function extracts the wallet name and chain from the wallet provider key and matches it
 * against the WalletConnect catalog to find the corresponding wallet entry, which includes
 * deep link information for mobile wallet connections.
 *
 * @param params.walletProviderKey - The wallet provider key to get the catalog wallet entry for.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to the WalletConnect catalog wallet entry, or undefined if not found.
 */
export const getWalletConnectCatalogWalletByWalletProviderKey = async (
  { walletProviderKey }: GetWalletConnectCatalogWalletByWalletProviderKeyParams,
  client = getDefaultClient()
): Promise<WalletConnectCatalogWallet | undefined> => {
  const { normalizedWalletNameWithChain } =
    splitWalletProviderKey(walletProviderKey);

  const chain = extractChainFromNormalizedName({
    normalizedWalletNameWithChain,
  });

  if (!chain) {
    return undefined;
  }

  // Remove chain suffix from wallet name (e.g., "metamaskevm" -> "metamask")
  let walletProviderName = normalizedWalletNameWithChain.toLowerCase();

  if (walletProviderName.endsWith(chain.toLowerCase())) {
    walletProviderName = walletProviderName.slice(0, -chain.length);
  }

  // Remove "wallet" substring (e.g., "trustwallet" -> "trust", "okxwallet" -> "okx")
  walletProviderName = walletProviderName.replace(/wallet/g, '');

  const walletConnectCatalog = await getWalletConnectCatalog(client);

  for (const [walletKey, wallet] of Object.entries(
    walletConnectCatalog.wallets
  )) {
    if (wallet.chain !== chain) {
      continue;
    }

    const walletKeyLower = walletKey.toLowerCase();
    const walletNameLower = wallet.name.toLowerCase().replace(/\s+/g, '');

    if (
      walletKeyLower.includes(walletProviderName) ||
      walletNameLower.includes(walletProviderName)
    ) {
      return wallet;
    }
  }

  return undefined;
};
